import { createCompany, getCompany, updateCompany } from '@/lib/api/companies/companies-api'
import { fetchPostalCode, PostalCodeException } from '@/lib/api/fetch-postal-code'
import { getAccessibleCompanies } from '@/lib/api/organization/organization-api'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setAccessibleCompanies } from '@/store/slices/authSlice'
import { useEffect, useState } from 'react'
import {
    MdApartment,
    MdArrowBack,
    MdBusiness,
    MdEmail,
    MdLanguage,
    MdLocationOn,
    MdPhone,
    MdSave,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface UnitFormData {
    name: string
    tradingName: string
    cnpj: string
    email: string
    phone: string
    website: string
    street: string
    number: string
    complement: string
    neighborhood: string
    city: string
    state: string
    zipCode: string
}

const initialFormData: UnitFormData = {
    name: '',
    tradingName: '',
    cnpj: '',
    email: '',
    phone: '',
    website: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
}

export default function UnitFormPage() {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { id } = useParams<{ id: string }>()
    const { user } = useAppSelector(state => state.auth)
    const isEditing = !!id && id !== 'nova'

    const [formData, setFormData] = useState<UnitFormData>(initialFormData)
    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(isEditing)
    const [loadingCep, setLoadingCep] = useState(false)

    useEffect(() => {
        if (isEditing && id) {
            loadUnit(parseInt(id))
        }
    }, [id, isEditing])

    const loadUnit = async (unitId: number) => {
        setLoadingData(true)
        try {
            const response = await getCompany(unitId)
            if (response.success && response.result) {
                const company = response.result
                setFormData({
                    name: company.name || '',
                    tradingName: company.tradingName || '',
                    cnpj: company.cnpj || '',
                    email: company.email || '',
                    phone: company.phone || '',
                    website: company.website || '',
                    street: company.street || '',
                    number: company.number || '',
                    complement: company.complement || '',
                    neighborhood: company.neighborhood || '',
                    city: company.city || '',
                    state: company.state || '',
                    zipCode: company.zipCode || '',
                })
            } else {
                toast.error('Unidade nao encontrada')
                navigate('/person/companies')
            }
        } catch (error) {
            toast.error('Erro ao carregar unidade')
            navigate('/person/companies')
        } finally {
            setLoadingData(false)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const formatCNPJ = (value: string) => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length <= 14) {
            return numbers
                .replace(/^(\d{2})(\d)/, '$1.$2')
                .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                .replace(/\.(\d{3})(\d)/, '.$1/$2')
                .replace(/(\d{4})(\d)/, '$1-$2')
        }
        return value
    }

    const formatPhone = (value: string) => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length <= 11) {
            if (numbers.length <= 10) {
                return numbers
                    .replace(/^(\d{2})(\d)/, '($1) $2')
                    .replace(/(\d{4})(\d)/, '$1-$2')
            }
            return numbers
                .replace(/^(\d{2})(\d)/, '($1) $2')
                .replace(/(\d{5})(\d)/, '$1-$2')
        }
        return value
    }

    const formatCEP = (value: string) => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length <= 8) {
            return numbers.replace(/^(\d{5})(\d)/, '$1-$2')
        }
        return value
    }

    const handleCepChange = async (value: string) => {
        const cleanCode = value.replace(/\D/g, '')
        const formatted = formatCEP(cleanCode)

        setFormData(prev => ({ ...prev, zipCode: cleanCode }))

        // Busca automatica quando CEP completo (8 digitos)
        if (cleanCode.length === 8) {
            setLoadingCep(true)
            try {
                const postalData = await fetchPostalCode(cleanCode)
                setFormData(prev => ({
                    ...prev,
                    zipCode: cleanCode,
                    street: postalData.logradouro || prev.street,
                    neighborhood: postalData.bairro || prev.neighborhood,
                    city: postalData.localidade || prev.city,
                    state: postalData.uf || prev.state,
                    complement: postalData.complemento || prev.complement,
                }))
            } catch (error) {
                if (error instanceof PostalCodeException) {
                    toast.error(error.message || 'CEP não encontrado')
                } else {
                    toast.error('Erro ao buscar CEP')
                }
            } finally {
                setLoadingCep(false)
            }
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Nome da unidade é obrigatório')
            return
        }

        setLoading(true)
        try {
            const data = {
                name: formData.name.trim(),
                tradingName: formData.tradingName.trim() || undefined,
                cnpj: formData.cnpj.replace(/\D/g, '') || undefined,
                email: formData.email.trim() || undefined,
                phone: formData.phone.replace(/\D/g, '') || undefined,
                website: formData.website.trim() || undefined,
                street: formData.street.trim() || undefined,
                number: formData.number.trim() || undefined,
                complement: formData.complement.trim() || undefined,
                neighborhood: formData.neighborhood.trim() || undefined,
                city: formData.city.trim() || undefined,
                state: formData.state.trim() || undefined,
                zipCode: formData.zipCode.replace(/\D/g, '') || undefined,
                organizationId: user?.company?.organizationId || undefined,
                // Herdar segment da company atual
                segment: user?.company?.segment || undefined,
            }

            let response
            if (isEditing && id) {
                response = await updateCompany(parseInt(id), data)
            } else {
                response = await createCompany(data as any)
            }

            if (response.success) {
                toast.success(isEditing ? 'Unidade atualizada com sucesso' : 'Unidade criada com sucesso')

                // Atualizar lista de empresas acessiveis no CompanySelector
                try {
                    const companiesResponse = await getAccessibleCompanies()
                    if (companiesResponse.success && companiesResponse.result) {
                        dispatch(setAccessibleCompanies(companiesResponse.result))
                    }
                } catch (err) {
                    console.error('Erro ao atualizar empresas acessiveis:', err)
                }

                navigate('/person/companies')
            } else {
                toast.error(response.error || 'Erro ao salvar unidade')
            }
        } catch (error) {
            toast.error('Erro ao salvar unidade')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex justify-center items-center min-h-[50vh]">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate('/person/companies')}
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-base-content flex items-center gap-2">
                        <MdApartment className="text-secondary" />
                        {isEditing ? 'Editar Unidade' : 'Nova Unidade'}
                    </h1>
                    <p className="text-sm text-base-content/60 mt-1">
                        {isEditing
                            ? 'Atualize os dados da unidade'
                            : 'Cadastre uma nova unidade/filial'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dados Basicos */}
                <div className="card bg-base-100 shadow-sm border border-base-300">
                    <div className="card-body">
                        <h2 className="card-title text-base flex items-center gap-2">
                            <MdBusiness className="text-secondary" />
                            Dados da Unidade
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Nome da Empresa *</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="input input-bordered"
                                    placeholder="Razão Social"
                                    required
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Nome Fantasia</span>
                                </label>
                                <input
                                    type="text"
                                    name="tradingName"
                                    value={formData.tradingName}
                                    onChange={handleChange}
                                    className="input input-bordered"
                                    placeholder="Nome Fantasia"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">CNPJ</span>
                                </label>
                                <input
                                    type="text"
                                    name="cnpj"
                                    value={formatCNPJ(formData.cnpj)}
                                    onChange={e => setFormData(prev => ({
                                        ...prev,
                                        cnpj: e.target.value.replace(/\D/g, ''),
                                    }))}
                                    className="input input-bordered"
                                    placeholder="00.000.000/0000-00"
                                    maxLength={18}
                                />
                            </div>


                        </div>
                    </div>
                </div>

                {/* Contato */}
                <div className="card bg-base-100 shadow-sm border border-base-300">
                    <div className="card-body">
                        <h2 className="card-title text-base flex items-center gap-2">
                            <MdPhone className="text-secondary" />
                            Contato
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Email</span>
                                </label>
                                <div className="relative">
                                    <MdEmail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl z-10 pointer-events-none" />
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="input input-bordered w-full pl-10"
                                        placeholder="contato@empresa.com"
                                    />
                                </div>
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Telefone</span>
                                </label>
                                <div className="relative">
                                    <MdPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl z-10 pointer-events-none" />
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formatPhone(formData.phone)}
                                        onChange={e => setFormData(prev => ({
                                            ...prev,
                                            phone: e.target.value.replace(/\D/g, ''),
                                        }))}
                                        className="input input-bordered w-full pl-10"
                                        placeholder="(00) 00000-0000"
                                        maxLength={15}
                                    />
                                </div>
                            </div>

                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text">Website</span>
                                </label>
                                <div className="relative">
                                    <MdLanguage className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl z-10 pointer-events-none" />
                                    <input
                                        type="url"
                                        name="website"
                                        value={formData.website}
                                        onChange={handleChange}
                                        className="input input-bordered w-full pl-10"
                                        placeholder="https://www.empresa.com"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Endereco */}
                <div className="card bg-base-100 shadow-sm border border-base-300">
                    <div className="card-body">
                        <h2 className="card-title text-base flex items-center gap-2">
                            <MdLocationOn className="text-secondary" />
                            Endereço
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">CEP</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="zipCode"
                                        value={formatCEP(formData.zipCode)}
                                        onChange={e => handleCepChange(e.target.value)}
                                        className="input input-bordered w-full"
                                        placeholder="00000-000"
                                        maxLength={9}
                                        disabled={loadingCep}
                                    />
                                    {loadingCep && (
                                        <span className="loading loading-spinner loading-sm absolute right-3 top-1/2 -translate-y-1/2" />
                                    )}
                                </div>
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        Digite o CEP para preencher automaticamente o endereço
                                    </span>
                                </label>
                            </div>

                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text">Rua</span>
                                </label>
                                <input
                                    type="text"
                                    name="street"
                                    value={formData.street}
                                    onChange={handleChange}
                                    className="input input-bordered"
                                    placeholder="Nome da rua"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Numero</span>
                                </label>
                                <input
                                    type="text"
                                    name="number"
                                    value={formData.number}
                                    onChange={handleChange}
                                    className="input input-bordered"
                                    placeholder="123"
                                />
                            </div>

                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text">Complemento</span>
                                </label>
                                <input
                                    type="text"
                                    name="complement"
                                    value={formData.complement}
                                    onChange={handleChange}
                                    className="input input-bordered"
                                    placeholder="Sala, Andar, etc."
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Bairro</span>
                                </label>
                                <input
                                    type="text"
                                    name="neighborhood"
                                    value={formData.neighborhood}
                                    onChange={handleChange}
                                    className="input input-bordered"
                                    placeholder="Bairro"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Cidade</span>
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className="input input-bordered"
                                    placeholder="Cidade"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Estado</span>
                                </label>
                                <input
                                    type="text"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    className="input input-bordered"
                                    placeholder="UF"
                                    maxLength={2}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Acoes */}
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/person/companies')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        {isEditing ? 'Atualizar' : 'Criar Unidade'}
                    </button>
                </div>
            </form>
        </div>
    )
}
